import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Upload,
  BookOpen,
  FileText,
  Image,
  Table,
  CheckCircle,
  Clock,
  AlertCircle,
  ChevronRight,
  Trash2,
  ChevronDown,
} from "lucide-react";
import { knowledgeAPI } from "../api";
import { useAuthStore } from "../store/authStore";
import toast from "react-hot-toast";
import clsx from "clsx";

// Helper function to map status key to text color class
const getStatusColorClass = (statusKey) => {
  switch (statusKey) {
    case "approved":
      return "text-green-500";
    case "pending_review":
      return "text-yellow-500";
    case "processing":
      return "text-blue-500";
    case "rejected":
      return "text-red-500";
    case "archived":
      return "text-slate-500";
    default:
      return "text-slate-500";
  }
};

const typeConfig = {
  manual: { label: "Manual", icon: BookOpen, color: "text-blue-500" },
  sop: { label: "SOP", icon: FileText, color: "text-green-500" },
  guide: { label: "Guide", icon: BookOpen, color: "text-cyan-500" },
  troubleshooting: {
    label: "Troubleshooting",
    icon: AlertCircle,
    color: "text-yellow-500",
  },
  specification: {
    label: "Specification",
    icon: Table,
    color: "text-purple-500",
  },
  diagram: { label: "Diagram", icon: Image, color: "text-pink-500" },
  safety: { label: "Safety", icon: AlertCircle, color: "text-red-500" },
  other: { label: "Other", icon: FileText, color: "text-slate-500" },
};

const statusConfig = {
  processing: { label: "Processing", color: "badge-primary", icon: Clock },
  pending_review: {
    label: "Pending Review",
    color: "badge-warning",
    icon: Clock,
  },
  approved: { label: "Approved", color: "badge-success", icon: CheckCircle },
  rejected: { label: "Rejected", color: "badge-danger", icon: AlertCircle },
  archived: { label: "Archived", color: "badge-neutral", icon: FileText },
};

const DocumentListItem = ({ doc, index, isAdmin, onDelete }) => {
  const type = typeConfig[doc.documentType] || typeConfig.other;
  const status = statusConfig[doc.status] || statusConfig.processing;
  const TypeIcon = type.icon;
  const StatusIcon = status.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Link
        to={`/knowledge/${doc._id}`}
        className="grid grid-cols-12 items-center p-4 text-sm border-b border-slate-100 transition-colors hover:bg-slate-50 group last:border-b-0"
      >
        {/* Column 1: Title & Type (span 4) */}
        <div className="col-span-4 flex items-center gap-3 min-w-0 pr-4">
          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
            <TypeIcon className={clsx("w-4 h-4", type.color)} />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-slate-800 group-hover:text-blue-600 transition-colors truncate">
              {doc.title}
            </p>
            <p className="text-xs text-slate-500">{type.label}</p>
          </div>
        </div>

        {/* Column 2: Status (span 2) */}
        <div className="col-span-2 flex items-center gap-2 text-slate-700">
          {StatusIcon && (
            <StatusIcon
              className={clsx("w-4 h-4", getStatusColorClass(doc.status))}
            />
          )}
          <span>{status.label}</span>
        </div>

        {/* Column 3: Category (span 2) */}
        <div className="col-span-2 text-slate-700 truncate">
          {doc.category || "N/A"}
        </div>

        {/* Column 4: Confidence (span 2) */}
        <div className="col-span-2 text-slate-500">
          {doc.extraction?.confidenceScore !== undefined ? (
            <span
              className={clsx(
                "font-medium",
                doc.extraction.confidenceScore >= 0.8
                  ? "text-green-600"
                  : doc.extraction.confidenceScore >= 0.6
                    ? "text-yellow-600"
                    : "text-red-600",
              )}
            >
              {`${Math.round(doc.extraction.confidenceScore * 100)}%`}
            </span>
          ) : (
            <span>-</span>
          )}
        </div>

        {/* Column 5: Created Date (span 1) */}
        <div className="col-span-1 text-slate-500 text-xs">
          {new Date(doc.createdAt).toLocaleDateString()}
        </div>

        {/* Column 6: Actions (span 1) */}
        <div className="col-span-1 flex justify-end items-center gap-2 pr-2">
          {isAdmin && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDelete(doc._id, doc.title);
              }}
              // Removed opacity-0 and group-hover classes to ensure visibility
              className="p-1.5 text-slate-400 rounded-md hover:text-red-600 hover:bg-red-100 transition-colors duration-200"
              title="Delete Document"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-blue-500 transition-transform group-hover:translate-x-0.5" />
        </div>
      </Link>
    </motion.div>
  );
};

const Knowledge = () => {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isTypeOpen, setIsTypeOpen] = useState(false);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const typeRef = useRef(null);
  const statusRef = useRef(null);

  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === "super_admin" || user?.role === "admin";

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (typeRef.current && !typeRef.current.contains(event.target))
        setIsTypeOpen(false);
      if (statusRef.current && !statusRef.current.contains(event.target))
        setIsStatusOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const { data: documentsData, isLoading } = useQuery({
    queryKey: ["knowledge", { type: typeFilter, status: statusFilter }],
    queryFn: () =>
      knowledgeAPI
        .getAll({
          type: typeFilter || undefined,
          status: statusFilter || undefined,
        })
        .then((res) => res.data),
  });

  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ["knowledge-search", search],
    queryFn: () =>
      knowledgeAPI.search({ q: search }).then((res) => res.data.data),
    enabled: search.length > 2,
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => knowledgeAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["knowledge"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-search"] });
      toast.success("Document deleted successfully");
    },
    onError: (err) =>
      toast.error(err.response?.data?.error || "Failed to delete"),
  });

  const handleDelete = (id, title) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${title}"? This cannot be undone.`,
      )
    ) {
      deleteMutation.mutate(id);
    }
  };

  const documents =
    search.length > 2 ? searchResults : documentsData?.data || [];

  const TypeIconToDisplay = typeFilter ? typeConfig[typeFilter]?.icon : null;
  const typeColorToDisplay = typeFilter ? typeConfig[typeFilter]?.color : null;
  const StatusIconToDisplay = statusFilter
    ? statusConfig[statusFilter]?.icon
    : null;
  const statusColorToDisplay = statusFilter
    ? getStatusColorClass(statusFilter)
    : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Knowledge Base</h1>
          <p className="text-slate-500">
            Machine manuals, SOPs, and documentation
          </p>
        </div>
        <Link to="/knowledge/upload" className="btn btn-primary">
          <Upload className="w-4 h-4" />
          Upload Document
        </Link>
      </div>

      {/* Filters Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative w-full sm:w-96">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search documents..."
            className="input w-full h-10 pl-12"
          />
          {isSearching && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2">
              <div className="w-4 h-4 border-2 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Type Dropdown */}
          <div ref={typeRef} className="relative">
            <button
              onClick={() => setIsTypeOpen(!isTypeOpen)}
              className="btn btn-secondary h-10 w-full sm:w-auto justify-between"
            >
              <span className="flex items-center gap-2">
                {TypeIconToDisplay && (
                  <TypeIconToDisplay
                    className={clsx("w-4 h-4", typeColorToDisplay)}
                  />
                )}
                {typeFilter ? typeConfig[typeFilter]?.label : "All Types"}
              </span>
              <ChevronDown
                className={clsx(
                  "w-4 h-4 transition-transform",
                  isTypeOpen && "rotate-180",
                )}
              />
            </button>
            <AnimatePresence>
              {isTypeOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10"
                >
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setTypeFilter("");
                        setIsTypeOpen(false);
                      }}
                      className={clsx(
                        "flex items-center w-full px-4 py-2 text-sm text-left transition-colors",
                        typeFilter === ""
                          ? "bg-blue-50 text-blue-700"
                          : "text-slate-700 hover:bg-slate-50",
                      )}
                    >
                      All Types
                    </button>
                    {Object.keys(typeConfig).map((type) => {
                      const ConfigIcon = typeConfig[type].icon;
                      return (
                        <button
                          key={type}
                          onClick={() => {
                            setTypeFilter(type);
                            setIsTypeOpen(false);
                          }}
                          className={clsx(
                            "flex items-center w-full px-4 py-2 text-sm text-left transition-colors",
                            typeFilter === type
                              ? "bg-blue-50 text-blue-700"
                              : "text-slate-700 hover:bg-slate-50",
                          )}
                        >
                          <ConfigIcon
                            className={clsx(
                              "w-4 h-4 mr-3",
                              typeConfig[type].color,
                            )}
                          />
                          {typeConfig[type].label}
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {/* Status Dropdown */}
          <div ref={statusRef} className="relative">
            <button
              onClick={() => setIsStatusOpen(!isStatusOpen)}
              className="btn btn-secondary h-10 w-full sm:w-auto justify-between"
            >
              <span className="flex items-center gap-2">
                {statusFilter && statusConfig[statusFilter] ? (
                  <>
                    {React.createElement(statusConfig[statusFilter].icon, {
                      className: clsx("w-4 h-4", statusColorToDisplay),
                    })}
                    {statusConfig[statusFilter]?.label}
                  </>
                ) : (
                  "All Status"
                )}
              </span>
              <ChevronDown
                className={clsx(
                  "w-4 h-4 transition-transform",
                  isStatusOpen && "rotate-180",
                )}
              />
            </button>
            <AnimatePresence>
              {isStatusOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10"
                >
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setStatusFilter("");
                        setIsStatusOpen(false);
                      }}
                      className={clsx(
                        "flex items-center w-full px-4 py-2 text-sm text-left transition-colors",
                        statusFilter === ""
                          ? "bg-blue-50 text-blue-700"
                          : "text-slate-700 hover:bg-slate-50",
                      )}
                    >
                      All Status
                    </button>
                    {Object.keys(statusConfig).map((statusKey) => {
                      const ConfigIcon = statusConfig[statusKey].icon;
                      const color = getStatusColorClass(statusKey);
                      return (
                        <button
                          key={statusKey}
                          onClick={() => {
                            setStatusFilter(statusKey);
                            setIsStatusOpen(false);
                          }}
                          className={clsx(
                            "flex items-center w-full px-4 py-2 text-sm text-left transition-colors",
                            statusFilter === statusKey
                              ? "bg-blue-50 text-blue-700"
                              : "text-slate-700 hover:bg-slate-50",
                          )}
                        >
                          {ConfigIcon && (
                            <ConfigIcon
                              className={clsx("w-4 h-4 mr-3", color)}
                            />
                          )}
                          {statusConfig[statusKey].label}
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="card overflow-hidden p-0">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="grid grid-cols-12 items-center p-4 border-b border-slate-200 last:border-b-0"
            >
              <div className="col-span-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg skeleton" />
                <div className="flex-1">
                  <div className="h-5 w-48 skeleton mb-1" />
                  <div className="h-3 w-24 skeleton" />
                </div>
              </div>
              <div className="col-span-2">
                <div className="h-4 w-20 skeleton" />
              </div>
              <div className="col-span-2">
                <div className="h-4 w-28 skeleton" />
              </div>
              <div className="col-span-2">
                <div className="h-4 w-12 skeleton" />
              </div>
              <div className="col-span-1">
                <div className="h-4 w-16 skeleton" />
              </div>
              <div className="col-span-1" />
            </div>
          ))}
        </div>
      ) : documents && documents.length > 0 ? (
        <div className="card overflow-hidden p-0">
          {/* Table Header */}
          <div className="grid grid-cols-12 items-center text-xs font-semibold uppercase text-slate-500 bg-slate-50 border-b border-slate-200 px-4 py-3">
            <div className="col-span-4">Document / Type</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-2">Confidence</div>
            <div className="col-span-1">Created</div>
            <div className="col-span-1"></div> {/* Action column */}
          </div>
          {/* List Body */}
          <div>
            <AnimatePresence>
              {documents.map((doc, index) => (
                <DocumentListItem
                  key={doc._id}
                  doc={doc}
                  index={index}
                  isAdmin={isAdmin}
                  onDelete={handleDelete}
                />
              ))}
            </AnimatePresence>
          </div>
        </div>
      ) : (
        <div className="card p-12 text-center">
          <BookOpen className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-800 mb-2">
            No documents found
          </h3>
          <p className="text-slate-500 mb-4">
            {search
              ? "Try a different search term or adjust filters"
              : "Upload your first document to get started"}
          </p>
          <Link to="/knowledge/upload" className="btn btn-primary">
            <Upload className="w-4 h-4" />
            Upload Document
          </Link>
        </div>
      )}
    </div>
  );
};

export default Knowledge;
